import { useRouter } from 'next/router'
import React, { Fragment, useState } from 'react'

import MapCard from '@/modules/investment/why-invest/worldMapPages/MapCard'
import MapContent from '@/modules/investment/why-invest/worldMapPages/MapContent'
import {
  getWhySezInfographicEconomicGrowth,
  getWhySezInfographicNaturalResources,
  getWhySezInfographicPopulation,
  getWhySezInfographicStrategicLocation,
} from '@/services/why-sez'
import { ImageResponsive } from '@/types/common'
import clsxm from '@/utils/clsxm'
import { useAppSelector } from '@/utils/hooks'

import economicGrowthCardIcon from '~/assets/images/investment/why-invest/economic-growth-card-icon.png'
import economicGrowthCardIconShadow from '~/assets/images/investment/why-invest/economic-growth-card-icon-shadow.png'
import naturalResourceCardIcon from '~/assets/images/investment/why-invest/natural-resource-card-icon.png'
import naturalResourceCardIconShadow from '~/assets/images/investment/why-invest/natural-resource-card-icon-shadow.png'
import strategicLocationCardIcon from '~/assets/images/investment/why-invest/strategic-location-card-icon.png'
import strategicLocationCardIconShadow from '~/assets/images/investment/why-invest/strategic-location-card-icon-shadow.png'
const WorldMaps = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const router = useRouter()
  const { data: infographicStrategicLocation } = useAppSelector(
    getWhySezInfographicStrategicLocation.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )
  const { data: infographicPopulation } = useAppSelector(
    getWhySezInfographicPopulation.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )

  const { data: infographicNaturalResources } = useAppSelector(
    getWhySezInfographicNaturalResources.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )
  const { data: infographicEconomicGrowth } = useAppSelector(
    getWhySezInfographicEconomicGrowth.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )

  const tabs = [
    {
      id: 1,
      title: infographicStrategicLocation?.data.title,
      images: infographicStrategicLocation?.data.mapImageFiles,
      borderColor: 'border-red',
      cardIcon: strategicLocationCardIcon.src,
      cardIconShadow: strategicLocationCardIconShadow.src,
      cardIconStyle: 'h-[24px] w-[21px] md:h-[48px] md:w-[42px]',
      cardIconShadowStyle: 'map-card-type-first-ornament',
      description: infographicStrategicLocation?.data.description ?? '',
      strategicLocation: {
        cardImageFiles: infographicStrategicLocation?.data.cardImageFiles,
        contentImageFiles: infographicStrategicLocation?.data.contentImageFiles,
      },
      legendImageFiles: infographicStrategicLocation?.data.legendImageFiles,
      spaceContentStyle: 'map-card-type-first-gap',
      cardContainerStyle: 'map-card-type-first-card-container',
    },
    {
      id: 2,
      title: infographicNaturalResources?.data.title,
      images: infographicNaturalResources?.data.mapImageFiles,
      borderColor: 'border-yellow',
      cardIcon: naturalResourceCardIcon.src,
      cardIconShadow: naturalResourceCardIconShadow.src,
      cardIconStyle: 'size-[24px] md:size-[48px]',
      cardIconShadowStyle: 'map-card-type-second-ornament',
      description: infographicNaturalResources?.data.description,
      naturalResources: {
        cardImageFiles: infographicNaturalResources?.data.cardImageFiles,
      },
      legendImageFiles: infographicNaturalResources?.data.legendImageFiles,
      spaceContentStyle: 'map-card-type-second-gap',
      cardContainerStyle: 'map-card-type-second-card-container',
    },
    {
      id: 3,
      title: infographicPopulation?.data.title,
      images: infographicPopulation?.data.mapImageFiles,
      borderColor: 'border-orange-card',
      population: {
        number: infographicPopulation?.data.number ?? 0,
        unitNumber: infographicPopulation?.data.unitNumber ?? '',
        items: infographicPopulation?.data.items ?? [],
        cardImageFiles: infographicPopulation?.data.cardImageFiles,
      },
      legendImageFiles: infographicPopulation?.data.legendImageFiles,
      spaceContentStyle: 'map-card-type-third-gap',
      cardContainerStyle: 'map-card-type-third-card-container',
    },
    {
      id: 4,
      title: infographicEconomicGrowth?.data.title,
      images: infographicEconomicGrowth?.data.mapImageFiles,
      borderColor: 'border-red',
      cardIcon: economicGrowthCardIcon.src,
      cardIconShadow: economicGrowthCardIconShadow.src,
      cardIconStyle: 'size-[48px]',
      cardIconShadowStyle: 'map-card-type-forth-ornament',
      imageContent: infographicEconomicGrowth?.data.contentImageFiles,
      legendImageFiles: infographicEconomicGrowth?.data.legendImageFiles,
      spaceContentStyle: 'map-card-type-forth-gap',
      cardContainerStyle: 'map-card-type-forth-card-container',
    },
  ]

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  if (
    infographicStrategicLocation?.data &&
    infographicNaturalResources?.data &&
    infographicPopulation?.data &&
    infographicEconomicGrowth?.data
  ) {
    return (
      <section className='relative h-auto lg:h-[calc(100vh-75px)]'>
        <div className='map-tabs'>
          {tabs.map((tab, idx) => (
            <button
              key={tab.id}
              onClick={() => handlePageChange(idx)}
              className={clsxm(
                'btn btn--pill p-2 text-[8px] md:text-xs',
                currentPage == idx
                  ? 'bg-orange text-dark hover:bg-orange'
                  : 'bg-red text-white hover:bg-orange hover:text-dark'
              )}
            >
              {tab.title}
            </button>
          ))}
        </div>
        {tabs.map((tab, idx) => (
          <Fragment key={tab.id}>
            <MapContent
              images={tab.images as ImageResponsive}
              legendImageFiles={tab.legendImageFiles}
              active={currentPage === idx}
            />
            <MapCard
              data={tab}
              active={currentPage === idx}
            />
          </Fragment>
        ))}
      </section>
    )
  }
  return null
}

export default WorldMaps
